'use client'

import { Fragment, useRef, useState } from 'react'
import {
  CarouselContainer,
  CarouselWrapper,
  Dot,
  DotContainer,
  FlexPaginate,
  LeftOutlinedWrapper,
  RightOutlinedWrapper,
} from './dynamicCarousel.style'
import { RenderPaginationProps } from 'react-elastic-carousel'

interface RenderPagination extends RenderPaginationProps {
  onClick: any
}

const DynamicCarousel = ({
  className,
  children,
  breakPoints,
  itemPadding = [10, 10, 10, 0],
  itemsToScroll = 1,
  itemsToShow = 1,
  pagination = true,
  disabled,
  initialActiveIndex,
  customPagination,
  isRequiredDisplayDotted,
  showPaginationOnMobile,
  isArrowPaginate = true,
}: any) => {
  const [touchStartX, setTouchStartX] = useState<number | null>(0)
  const [touchEndX, setTouchEndX] = useState<number | null>(null)
  const [touchStartY, setTouchStartY] = useState<number | null>(0)
  const [touchEndY, setTouchEndY] = useState<number | null>(null)
  let carouselRef: any = useRef(null)
  // the required distance between touchStart and touchEnd to be detected as a swipe
  const minSwipeDistance = 10

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEndX(0) // otherwise the swipe is fired even with usual touch events
    setTouchStartX(e.targetTouches[0].clientX)
    setTouchEndY(0) // otherwise the swipe is fired even with usual touch events
    setTouchStartY(e.targetTouches[0].clientY)
  }

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEndX(e.targetTouches[0].clientX)
    setTouchEndY(e.targetTouches[0].clientY)
  }

  const onTouchEnd = () => {
    if (!touchStartX || !touchEndX || !touchStartY || !touchEndY) return
    const distanceX = touchStartX - touchEndX
    const distanceY = touchStartY - touchEndY
    const isLeftSwipe = distanceX > minSwipeDistance
    const isRightSwipe = distanceX < -minSwipeDistance

    if (isRightSwipe && Math.abs(distanceX) > distanceY) {
      return true
    }
    if (isLeftSwipe && distanceX > distanceY) {
      return true
    }
    return false
  }

  return (
    <Fragment>
      {!disabled ? (
        <CarouselContainer
          className={className}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
        >
          <CarouselWrapper
            breakPoints={breakPoints}
            itemPadding={itemPadding}
            itemsToScroll={itemsToScroll}
            itemsToShow={itemsToShow}
            initialActiveIndex={initialActiveIndex}
            isRTL={false}
            pagination={pagination}
            ref={carouselRef}
            showEmptySlots
            preventDefaultTouchmoveEvent={onTouchEnd()}
            isRequiredDisplayDotted={isRequiredDisplayDotted}
            renderPagination={({
              pages,
              activePage,
              onClick,
            }: RenderPagination) => {
              if (customPagination) {
                return customPagination({ pages, activePage, onClick })
              }
              return (
                <FlexPaginate $show={showPaginationOnMobile}>
                  {isArrowPaginate && (
                    //@ts-ignore Antdesign types incompatible with react@19
                    <LeftOutlinedWrapper
                      style={{
                        display: showPaginationOnMobile ? 'block' : undefined,
                      }}
                      $disabled={activePage === 0}
                      onClick={() => carouselRef.current.slidePrev()}
                    />
                  )}
                  <DotContainer>
                    {pages.map((page) => {
                      const isActivePage = activePage === page
                      return (
                        <Dot
                          key={page}
                          onClick={() => onClick(page)}
                          $isActive={isActivePage}
                        />
                      )
                    })}
                  </DotContainer>
                  {isArrowPaginate && (
                    //@ts-ignore Antdesign types incompatible with react@19
                    <RightOutlinedWrapper
                      style={{
                        display: showPaginationOnMobile ? 'block' : undefined,
                      }}
                      $disabled={activePage === pages.length - 1}
                      onClick={() => carouselRef.current.slideNext()}
                    />
                  )}
                </FlexPaginate>
              )
            }}
          >
            {children}
          </CarouselWrapper>
        </CarouselContainer>
      ) : (
        <Fragment> {children}</Fragment>
      )}
    </Fragment>
  )
}

export default DynamicCarousel
